// ResumeBuilder.js
import React, { useState, useRef, useEffect } from 'react';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { saveAs } from 'file-saver';


const ResumeBuilder2 = () => {
  // Form state with expanded fields
  const [formData, setFormData] = useState({
    name: '',
    headline: '',
    location: '',
    phone: '',
    email: '',
    portfolio: '',
    linkedin: '',
    github: '',
    jobTitle: '',
    jobDescription: '',
    education: [{
      degree: '',
      institution: '',
      location: '',
      startYear: '',
      endYear: '',
      gpa: '',
      fieldOfStudy: ''
    }],
    experience: [{
      title: '',
      company: '',
      location: '',
      startDate: '',
      endDate: '',
      achievements: ['', '', '']
    }],
    projects: [{
      title: '',
      description: '',
      technologies: '',
      startDate: '',
      endDate: '',
      achievements: ['', '']
    }],
    academicProjects: [{
      title: '',
      course: '',
      description: '',
      technologies: '',
      year: ''
    }],
    skills: {
      technical: ['', '', '', ''],
      soft: ['', '', ''],
      tools: ['', '', '']
    },
    certifications: [{
      name: '',
      issuer: '',
      date: '',
      id: ''
    }],
    awards: [{
      title: '',
      issuer: '',
      date: '',
      description: ''
    }],
    languages: [{
      name: '',
      level: ''
    }],
    interests: ['', '']
  });

  // Resume states
  const [generatedResume, setGeneratedResume] = useState(null);
  const [resumeText, setResumeText] = useState('');
  const [atsScore, setAtsScore] = useState(null);
  const [matchedKeywords, setMatchedKeywords] = useState([]);
  const [missedKeywords, setMissedKeywords] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('modern');
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [optimizationSuggestions, setOptimizationSuggestions] = useState(null);
  const [jobKeywords, setJobKeywords] = useState([]);
  const [summary, setSummary] = useState('');
  const resumeRef = useRef(null);
  
  // A4 page dimensions in mm
  const A4_WIDTH = 210;
  const A4_HEIGHT = 297;
  
  // Analyze job description whenever it changes
  useEffect(() => {
    if (formData.jobDescription) {
      const keywords = extractKeywords(formData.jobDescription);
      setJobKeywords(keywords);
    }
  }, [formData.jobDescription]);

  // Handle form input changes
  const handleInputChange = (e, section, index, subField, subIndex) => {
    const { name, value } = e.target;
    
    if (section) {
      // Handle nested structures
      if (subField && typeof subIndex !== 'undefined') {
        // For arrays within objects within arrays (e.g., experience[0].achievements[0])
        setFormData(prev => {
          const newData = {...prev};
          newData[section][index][subField][subIndex] = value;
          return newData;
        });
      } else if (subField) {
        // For objects within arrays (e.g., skills.technical)
        setFormData(prev => {
          const newData = {...prev};
          newData[section][subField][index] = value;
          return newData;
        });
      } else {
        // For arrays like education, experience
        setFormData(prev => {
          const newData = {...prev};
          newData[section][index][name] = value;
          return newData;
        });
      }
    } else {
      // For simple fields
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Handle skills input changes (special case for nested objects)
  const handleSkillsChange = (category, index, value) => {
    setFormData(prev => {
      const newSkills = {...prev.skills};
      newSkills[category][index] = value;
      return {
        ...prev,
        skills: newSkills
      };
    });
  };

  // Add more items to arrays
  const addItem = (section, subField) => {
    if (subField) {
      // For nested arrays (e.g., skills.technical)
      setFormData(prev => {
        const newData = {...prev};
        newData[section][subField].push('');
        return newData;
      });
    } else {
      switch(section) {
        case 'education':
          setFormData(prev => ({
            ...prev,
            education: [...prev.education, { 
              degree: '', 
              institution: '', 
              location: '', 
              startYear: '',
              endYear: '',
              gpa: '',
              fieldOfStudy: ''
            }]
          }));
          break;
        case 'experience':
          setFormData(prev => ({
            ...prev,
            experience: [...prev.experience, { 
              title: '', 
              company: '', 
              location: '', 
              startDate: '',
              endDate: '',
              achievements: ['', '', ''] 
            }]
          }));
          break;
        case 'projects':
          setFormData(prev => ({
            ...prev,
            projects: [...prev.projects, { 
              title: '', 
              description: '', 
              technologies: '',
              startDate: '',
              endDate: '',
              achievements: ['', ''] 
            }]
          }));
          break;
        case 'academicProjects':
          setFormData(prev => ({
            ...prev,
            academicProjects: [...prev.academicProjects, { 
              title: '', 
              course: '', 
              description: '',
              technologies: '',
              year: ''
            }]
          }));
          break;
        case 'certifications':
          setFormData(prev => ({
            ...prev,
            certifications: [...prev.certifications, { 
              name: '', 
              issuer: '', 
              date: '',
              id: ''
            }]
          }));
          break;
        case 'awards':
          setFormData(prev => ({
            ...prev,
            awards: [...prev.awards, { 
              title: '', 
              issuer: '', 
              date: '',
              description: ''
            }]
          }));
          break;
        case 'languages':
          setFormData(prev => ({
            ...prev,
            languages: [...prev.languages, { name: '', level: '' }]
          }));
          break;
        case 'interests':
          setFormData(prev => ({
            ...prev,
            interests: [...prev.interests, '']
          }));
          break;
        default:
          break;
      }
    }
  };

  // Add achievement to experience or project
  const addAchievement = (section, index) => {
    setFormData(prev => {
      const newData = {...prev};
      newData[section][index].achievements.push('');
      return newData;
    });
  };

  // Remove items from arrays
  const removeItem = (section, index, subField, subIndex) => {
    if (subField && typeof subIndex !== 'undefined') {
      // For arrays within objects within arrays (e.g., experience[0].achievements[0])
      setFormData(prev => {
        const newData = {...prev};
        if (newData[section][index][subField].length > 1) {
          newData[section][index][subField].splice(subIndex, 1);
        } else {
          newData[section][index][subField][0] = '';
        }
        return newData;
      });
    } else if (subField) {
      // For objects within arrays (e.g., skills.technical)
      setFormData(prev => {
        const newData = {...prev};
        if (newData[section][subField].length > 1) {
          newData[section][subField].splice(index, 1);
        }
        return newData;
      });
    } else {
      // For arrays like education, experience
      setFormData(prev => {
        const newData = {...prev};
        if (newData[section].length > 1) {
          newData[section].splice(index, 1);
        }
        return newData;
      });
    }
  };

  // Extract keywords from job description
  const extractKeywords = (text) => {
    // More comprehensive skill extraction
    const skillsByCategory = {
      technical: [
        'python', 'java', 'javascript', 'typescript', 'react', 'angular', 'vue', 'node.js', 'express', 
        'django', 'flask', 'spring', 'hibernate', 'sql', 'nosql', 'mongodb', 'firebase', 'aws', 'azure', 
        'google cloud', 'docker', 'kubernetes', 'ci/cd', 'git', 'jenkins', 'agile', 'scrum', 'jira', 
        'confluence', 'html', 'css', 'sass', 'less', 'webpack', 'babel', 'redux', 'graphql', 'rest api',
        'machine learning', 'deep learning', 'nlp', 'computer vision', 'data analysis', 'data science',
        'statistics', 'r', 'matlab', 'tableau', 'power bi', 'excel', 'vba', 'php', 'laravel', 'symfony',
        'wordpress', 'drupal', 'magento', 'shopify', 'woocommerce', 'seo', 'sem', 'digital marketing',
        'content writing', 'copywriting', 'editing', 'proofreading', 'ux/ui', 'figma', 'adobe xd',
        'sketch', 'photoshop', 'illustrator', 'indesign', 'premiere pro', 'after effects', 'blender',
        'unity', 'unreal engine', 'c++', 'c#', 'swift', 'kotlin', 'objective-c', 'ruby', 'rails',
        'golang', 'rust', 'scala', 'hadoop', 'spark', 'kafka', 'elasticsearch', 'logstash', 'kibana',
        'terraform', 'ansible', 'puppet', 'chef', 'nginx', 'apache', 'linux', 'unix', 'windows', 'macos'
      ],
      soft: [
        'communication', 'teamwork', 'leadership', 'problem-solving', 'critical thinking', 'time management',
        'organization', 'adaptability', 'flexibility', 'creativity', 'innovation', 'analytical skills',
        'interpersonal skills', 'customer service', 'presentation skills', 'verbal communication',
        'written communication', 'decision making', 'conflict resolution', 'negotiation', 'persuasion',
        'emotional intelligence', 'empathy', 'active listening', 'attention to detail', 'multitasking',
        'prioritization', 'stress management', 'work ethic', 'self-motivation', 'reliability',
        'responsibility', 'initiative', 'integrity', 'honesty', 'accountability', 'mentoring', 'coaching',
        'training', 'strategic thinking', 'project management', 'budget management', 'resource allocation',
        'risk assessment', 'stakeholder management', 'client relationship management'
      ],
      industry: [
        'healthcare', 'finance', 'banking', 'insurance', 'retail', 'e-commerce', 'education', 'government',
        'manufacturing', 'logistics', 'transportation', 'hospitality', 'tourism', 'real estate', 'construction',
        'energy', 'oil and gas', 'renewable energy', 'telecommunications', 'media', 'entertainment',
        'advertising', 'marketing', 'public relations', 'legal', 'consulting', 'pharmaceuticals', 'biotech',
        'agriculture', 'food and beverage', 'automotive', 'aerospace', 'defense', 'cybersecurity',
        'information technology', 'artificial intelligence', 'blockchain', 'cryptocurrency', 'iot',
        'augmented reality', 'virtual reality', 'gaming', 'sports', 'fitness', 'wellness', 'beauty',
        'fashion', 'luxury goods', 'nonprofit', 'social services', 'environmental services'
      ]
    };
    
    const foundSkills = {
      technical: [],
      soft: [],
      industry: []
    };
    
    // Clean text for better matching
    const cleanText = text.toLowerCase().replace(/[^\w\s]/g, ' ');
    
    // Find skills by category
    Object.keys(skillsByCategory).forEach(category => {
      skillsByCategory[category].forEach(skill => {
        if (cleanText.includes(skill.toLowerCase())) {
          foundSkills[category].push(skill);
        }
      });
    });
    
    // Extract phrases for more context-aware keywords
    const phrases = [
      /\b(\w+\s\w+)\s(specialist|manager|coordinator|assistant|lead|consultant|associate|analyst|director)\b/gi,
      /\b(\d+)\s*\+?\s*years?\s+(?:of)?\s*experience\b/gi,
      /\b(?:proficient|fluent|experienced|skilled)\s+in\s+(\w+(?:\s+\w+){0,3})\b/gi,
      /\b(bachelor's|master's|phd|doctorate)\s+(?:degree)?\s+in\s+(\w+(?:\s+\w+){0,3})\b/gi,
      /\b(certifications?|qualifications?)\s+in\s+(\w+(?:\s+\w+){0,3})\b/gi
    ];
    
    const importantPhrases = [];
    phrases.forEach(regex => {
      const matches = [...text.matchAll(regex)];
      matches.forEach(match => {
        importantPhrases.push(match[0]);
      });
    });
    
    // Combine all found keywords
    const allSkills = [
      ...foundSkills.technical, 
      ...foundSkills.soft, 
      ...foundSkills.industry,
      ...importantPhrases
    ];
    
    // Remove duplicates
    return [...new Set(allSkills)];
  };

  // Extract years of experience from job description
  const extractYearsOfExperience = (text) => {
    const match = text.match(/(\d+)[\s-]*years?[\s]*(?:of)?[\s]*experience/i);
    return match ? match[1] : '3-5';
  };

  // Generate professional summary based on job description and title
  const generateProfessionalSummary = () => {
    const { jobTitle, jobDescription } = formData;
    
    if (!jobTitle || !jobDescription) {
      alert("Please provide job title and description to generate a summary");
      return;
    }
    
    // Extract key requirements from job description
    const keywords = extractKeywords(jobDescription);
    const yearsExp = extractYearsOfExperience(jobDescription);
    
    // Identify industry
    let industry = 'technology';
    if (jobDescription.toLowerCase().includes('health') || jobDescription.toLowerCase().includes('medical')) {
      industry = 'healthcare';
    } else if (jobDescription.toLowerCase().includes('finance') || jobDescription.toLowerCase().includes('banking')) {
      industry = 'finance';
    } else if (jobDescription.toLowerCase().includes('marketing') || jobDescription.toLowerCase().includes('sales')) {
      industry = 'marketing';
    } else if (jobDescription.toLowerCase().includes('education') || jobDescription.toLowerCase().includes('teaching')) {
      industry = 'education';
    }
    
    // Generate dynamic summaries based on job title category
    let summary = '';
    
    // Technical roles
    if (jobTitle.toLowerCase().includes('developer') || 
        jobTitle.toLowerCase().includes('engineer') || 
        jobTitle.toLowerCase().includes('programmer')) {
      
      const techSkills = keywords.filter(k => k.length > 3).slice(0, 4).join(', ');
      
      summary = `Results-driven ${jobTitle} with ${yearsExp}+ years of experience developing innovative solutions and writing clean, efficient code. Proficient in ${techSkills} with a strong track record of delivering high-quality applications on time and within scope. Passionate about solving complex problems and continually expanding technical expertise to stay ahead of industry trends.`;
    } 
    // Management roles
    else if (jobTitle.toLowerCase().includes('manager') || 
             jobTitle.toLowerCase().includes('director') || 
             jobTitle.toLowerCase().includes('lead')) {
      
      summary = `Dynamic ${jobTitle} with ${yearsExp}+ years of experience driving organizational success through strategic leadership and team development. Proven track record of optimizing processes, managing resources efficiently, and delivering measurable results in the ${industry} sector. Skilled in stakeholder communication and building collaborative environments that foster innovation and achieve business objectives.`;
    }
    // Marketing/Sales
    else if (jobTitle.toLowerCase().includes('marketing') || 
             jobTitle.toLowerCase().includes('sales')) {
      
      summary = `Strategic ${jobTitle} with ${yearsExp}+ years of experience driving revenue growth and brand awareness in competitive markets. Expert in developing data-driven campaigns that deliver measurable ROI and exceed targets. Adept at identifying customer needs and creating compelling value propositions that resonate with target audiences and convert prospects into loyal customers.`;
    }
    // Analyst roles
    else if (jobTitle.toLowerCase().includes('analyst') || 
             jobTitle.toLowerCase().includes('data')) {
      
      summary = `Detail-oriented ${jobTitle} with ${yearsExp}+ years of experience transforming complex data into actionable business insights. Proficient in advanced analytics techniques, data visualization, and statistical analysis. Demonstrated ability to collaborate with cross-functional teams to identify opportunities, solve business problems, and drive evidence-based decision making.`;
    }
    // Default catch-all
    else {
      summary = `Dedicated ${jobTitle} professional with ${yearsExp}+ years of experience in the ${industry} industry. Combines strong ${keywords.slice(0, 2).join(' and ')} skills with exceptional ${keywords.slice(2, 4).join(' and ')} abilities to deliver outstanding results. Committed to continuous improvement and professional growth while contributing to organizational success through ${keywords.slice(4, 6).join(' and ')}.`;
    }
    
    setSummary(summary);
  };

  // Generate resume based on form data and job description
  const generateResume = () => {
    const { name, jobTitle, jobDescription } = formData;
    
    if (!name || !jobTitle) {
      alert("Please fill out the required fields");
      return;
    }
    
    // Generate a professional summary if not already set
    if (!summary) {
      generateProfessionalSummary();
    }
    
    // Set the generated resume
    setGeneratedResume({
      ...formData,
      summary: summary
    });
  };

  // Calculate ATS score
  const calculateATSScore = () => {
    const { jobDescription } = formData;
    if (!jobDescription || !resumeText) {
      alert("Please provide both job description and resume text");
      return;
    }
    
    // Extract important keywords from job description
    const jdWords = jobDescription.toLowerCase().match(/\w+/g) || [];
    const resumeWords = resumeText.toLowerCase().match(/\w+/g) || [];
    
    // More sophisticated keyword extraction
    const jobKeywords = extractKeywords(jobDescription);
    const importantKeywordRegexes = jobKeywords.map(kw => new RegExp(`\\b${kw.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')}\\b`, 'i'));
    
    // Find matches and calculate score
    const matched = new Set();
    const missed = new Set();
    
    // Check for exact keywords
    importantKeywordRegexes.forEach((regex, index) => {
      if (regex.test(resumeText.toLowerCase())) {
        matched.add(jobKeywords[index]);
      } else {
        missed.add(jobKeywords[index]);
      }
    });
    
    // Calculate weighted score
    // Core keywords are weighted more heavily
    const coreKeywords = jobKeywords.slice(0, 5);
    const coreMatched = coreKeywords.filter(k => matched.has(k));
    
    const baseScore = ((matched.size / (jobKeywords.length || 1)) * 70) + 
                      ((coreMatched.length / (coreKeywords.length || 1)) * 30);
    
    // Add bonus for having exact job title
    const titleBonus = resumeText.toLowerCase().includes(formData.jobTitle.toLowerCase()) ? 5 : 0;
    
    // Add bonus for proper formatting (sections, bullet points)
    const formattingBonus = 
      (resumeText.includes('Experience') || resumeText.includes('Work History')) ? 2 : 0 +
      (resumeText.includes('Education')) ? 2 : 0 +
      (resumeText.includes('Skills')) ? 2 : 0 +
      (/•|\*|-/.test(resumeText)) ? 4 : 0;
    
    const finalScore = Math.min(100, Number(baseScore) + titleBonus + formattingBonus);
    
    setAtsScore(Number(finalScore.toFixed(1)));
    setMatchedKeywords(Array.from(matched));
    setMissedKeywords(Array.from(missed).slice(0, 10)); // Show top 10 missed keywords
  };

  // Optimize resume based on job description
  const optimizeResume = () => {
    setIsOptimizing(true);
    
    // Simulating ML-based analysis with a timeout
    setTimeout(() => {
      const { jobDescription } = formData;
      const keywords = extractKeywords(jobDescription);
      
      // More sophisticated analysis of the resume
      const resumeLower = resumeText.toLowerCase();
      
      const missingKeywords = keywords.filter(k => !resumeLower.includes(k.toLowerCase()));
      const weakSections = [];
      
      // Check if experience section needs improvement
      if (!(/experience|work history/i.test(resumeText)) || 
          !(/achieved|improved|increased|decreased|managed|led|developed|created/i.test(resumeText))) {
        weakSections.push("Experience");
      }
      
      // Check if skills section needs improvement
      if (!(/skills|expertise|proficiencies/i.test(resumeText)) ||
          keywords.filter(k => resumeLower.includes(k.toLowerCase())).length < keywords.length * 0.5) {
        weakSections.push("Skills");
      }
      
      // Check if education section needs improvement
      if (!(/education|university|college|degree|bachelor|master|phd/i.test(resumeText))) {
        weakSections.push("Education");
      }
      
      // Check for quantifiable achievements
      const hasQuantifiableAchievements = /\d+%|\d+ percent|increased|decreased|reduced|improved by/i.test(resumeText);
      
      // Generate optimization suggestions
      const suggestions = {
        addKeywords: missingKeywords,
        improveSummary: `Consider highlighting your experience with ${keywords.slice(0, 3).join(', ')} in your summary.`,
        sectionImprovements: weakSections.map(section => `Your ${section} section could be strengthened to better match the job requirements.`),
        formatImprovements: [
          "Use bullet points for achievements to improve readability",
          "Quantify your achievements with numbers and percentages where possible",
          "Ensure consistent verb tense throughout the resume (past tense for previous roles)",
          "Use action verbs to start your bullet points (e.g., Developed, Managed, Increased)"
        ]
      };
      
      if (!hasQuantifiableAchievements) {
        suggestions.formatImprovements.push("Add quantifiable achievements (e.g., 'Increased sales by 20%', 'Reduced costs by $50K')");
      }
      
      setOptimizationSuggestions(suggestions);
      setIsOptimizing(false);
    }, 1500);
  };

  // Download resume as PDF with proper A4 formatting
  const downloadPDF = () => {
    if (!resumeRef.current) return;
    
    const element = resumeRef.current;
    const scale = 2; // Higher scale for better quality
    
    html2canvas(element, {
      scale: scale,
      useCORS: true,
      logging: false,
      allowTaint: true
    }).then(canvas => {
      // Create a new PDF with A4 dimensions
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      // Calculate aspect ratio to fit within A4
      const imgWidth = A4_WIDTH; // Full A4 width
      const imgHeight = (canvas.height * A4_WIDTH) / canvas.width;
      
      // If the height exceeds A4, we need to split into multiple pages
      if (imgHeight > A4_HEIGHT) {
        const pageCount = Math.ceil(imgHeight / A4_HEIGHT);
        const pageHeight = canvas.height / pageCount;
        
        for (let i = 0; i < pageCount; i++) {
          // For pages after the first, add a new page
          if (i > 0) {
            pdf.addPage();
          }
          
          // Create a temporary canvas for this page segment
          const tmpCanvas = document.createElement("canvas");
          tmpCanvas.width = canvas.width;
          tmpCanvas.height = pageHeight;
          
          const ctx = tmpCanvas.getContext("2d");
          // Draw the appropriate portion of the original canvas
          ctx.drawImage(
            canvas, 
            0, i * pageHeight, canvas.width, pageHeight,
            0, 0, canvas.width, pageHeight
          );
          
          // Add this segment to the PDF
          const imgData = tmpCanvas.toDataURL("image/png");
          pdf.addImage(imgData, "PNG", 0, 0, A4_WIDTH, A4_WIDTH * (pageHeight / canvas.width));
        }
      } else {
        // If it fits on one page, simply add the image
        const imgData = canvas.toDataURL("image/png");
        pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
      }
      
      // Save the PDF
      pdf.save(`${formData.name.replace(/\s+/g, '_')}_resume.pdf`);
    });
  };

  // Download resume as DOCX
  const downloadDOCX = () => {
    if (!resumeRef.current) return;
    
    try {
      // Create HTML content for docx conversion
      const content = resumeRef.current.innerHTML;
      
      // Create an HTML file with proper styling
      let htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>${formData.name} - Resume</title>
          <style>
            body { font-family: 'Times New Roman', Times, serif; margin: 0; padding: 20px; }
            h1, h2, h3 { font-family: 'Times New Roman', Times, serif; }
            h1 { font-size: 24pt; text-align: center; margin-bottom: 5pt; }
            h2 { font-size: 14pt; border-bottom: 1pt solid #000; margin-top: 15pt; margin-bottom: 5pt; }
            h3 { font-size: 12pt; margin-bottom: 2pt; margin-top: 10pt; }
            p { font-size: 11pt; margin: 2pt 0; }
            ul { margin-top: 0; margin-bottom: 5pt; }
            li { font-size: 11pt; }
            .section { margin-bottom: 10pt; }
            .header { text-align: center; margin-bottom: 15pt; }
            .contact { text-align: center; font-size: 11pt; }
          </style>
        </head>
        <body>
          ${content}
        </body>
        </html>
      `;
      
      // Create a blob with the HTML content
      const blob = new Blob([htmlContent], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
      saveAs(blob, `${formData.name.replace(/\s+/g, '_')}_resume.docx`);
    } catch (error) {
      console.error("Error generating DOCX:", error);
      alert("There was an error generating the DOCX file. Please try again.");
    }
  };

  // Template components with improved formatting
  const renderModernTemplate = () => {
    if (!generatedResume) return null;
    
    return (
      <div className="resume-modern">
        <div className="resume-header">
          <h1>{generatedResume.name}</h1>
          {generatedResume.headline && <p className="headline">{generatedResume.headline}</p>}
          <div className="contact-info">
            <p>
              {generatedResume.location && <span>{generatedResume.location} | </span>}
              {generatedResume.phone && <span>{generatedResume.phone} | </span>}
              {generatedResume.email && <span>{generatedResume.email}</span>}
            </p>
            <p>
              {generatedResume.linkedin && <span>{generatedResume.linkedin} | </span>}
              {generatedResume.github && <span>{generatedResume.github} | </span>}
              {generatedResume.portfolio && <span>{generatedResume.portfolio}</span>}
            </p>
          </div>
        </div>
        
        <div className="resume-section">
          <h2>Professional Summary</h2>
          <div className="summary-content">
            <p>{generatedResume.summary}</p>
          </div>
        </div>
        
        <div className="resume-section">
          <h2>Skills</h2>
          <div className="skills-content">
            {generatedResume.skills.technical.filter(s => s).length > 0 && (
              <div className="skills-category">
                <h3>Technical Skills</h3>
                <p>{generatedResume.skills.technical.filter(s => s).join(' • ')}</p>
              </div>
            )}
            
            {generatedResume.skills.soft.filter(s => s).length > 0 && (
              <div className="skills-category">
                <h3>Soft Skills</h3>
                <p>{generatedResume.skills.soft.filter(s => s).join(' • ')}</p>
              </div>
            )}
            
            {generatedResume.skills.tools.filter(s => s).length > 0 && (
              <div className="skills-category">
                <h3>Tools & Technologies</h3>
                <p>{generatedResume.skills.tools.filter(s => s).join(' • ')}</p>
              </div>
            )}
          </div>
        </div>
        
        <div className="resume-section">
          <h2>Experience</h2>
          {generatedResume.experience.map((exp, index) => (
            exp.company && (
              <div key={index} className="experience-item">
                <div className="job-header">
                  <h3>{exp.title}</h3>
                  <p className="company-info">
                    <span className="company-name">{exp.company}</span>
                    {exp.location && <span> | {exp.location}</span>}
                  </p>
                  <p className="date-range">
                    {exp.startDate && <span>{exp.startDate}</span>}
                    {exp.endDate && <span> - {exp.endDate}</span>}
                  </p>
                </div>
                <ul className="achievements">
                  {exp.achievements.filter(a => a).map((achievement, i) => (
                    <li key={i}>{achievement}</li>
                  ))}
                </ul>
              </div>
            )
          ))}
        </div>
        
        {generatedResume.projects.some(p => p.title) && (
          <div className="resume-section">
            <h2>Projects</h2>
            {generatedResume.projects.map((project, index) => (
              project.title && (
                <div key={index} className="project-item">
                  <div className="project-header">
                    <h3>{project.title}</h3>
                    {project.technologies && <p className="technologies">{project.technologies}</p>}
                    {(project.startDate || project.endDate) && (
                      <p className="date-range">
                        {project.startDate && <span>{project.startDate}</span>}
                        {project.endDate && <span> - {project.endDate}</span>}
                      </p>
                    )}
                  </div>
                  {project.description && <p className="project-description">{project.description}</p>}
                  {project.achievements.filter(a => a).length > 0 && (
                    <ul className="achievements">
                      {project.achievements.filter(a => a).map((achievement, i) => (
                        <li key={i}>{achievement}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )
            ))}
          </div>
        )}
        
        {generatedResume.academicProjects.some(p => p.title) && (
          <div className="resume-section">
            <h2>Academic Projects</h2>
            {generatedResume.academicProjects.map((project, index) => (
              project.title && (
                <div key={index} className="academic-project-item">
                  <div className="project-header">
                    <h3>{project.title}</h3>
                    {project.course && <p className="course">{project.course}</p>}
                    {project.technologies && <p className="technologies">{project.technologies}</p>}
                    {project.year && <p className="year">{project.year}</p>}
                  </div>
                  {project.description && <p className="project-description">{project.description}</p>}
                </div>
              )
            ))}
          </div>
        )}
        
        <div className="resume-section">
          <h2>Education</h2>
          {generatedResume.education.map((edu, index) => (
            edu.institution && (
              <div key={index} className="education-item">
                <div className="edu-header">
                  <h3>{edu.degree}</h3>
                  <p className="institution-info">
                    <span className="institution-name">{edu.institution}</span>
                    {edu.location && <span> | {edu.location}</span>}
                  </p>
                  <p className="date-range">
                    {edu.startYear && <span>{edu.startYear}</span>}
                    {edu.endYear && <span> - {edu.endYear}</span>}
                  </p>
                </div>
                {edu.fieldOfStudy && <p className="field-of-study">{edu.fieldOfStudy}</p>}
                {edu.gpa && <p className="gpa">GPA: {edu.gpa}</p>}
              </div>
            )
          ))}
        </div>
        
        {generatedResume.certifications.some(c => c.name) && (
          <div className="resume-section">
            <h2>Certifications</h2>
            {generatedResume.certifications.map((cert, index) => (
              cert.name && (
                <div key={index} className="certification-item">
                  <h3>{cert.name}</h3>
                  <p>
                    {cert.issuer && <span>{cert.issuer}</span>}
                    {cert.date && <span> | {cert.date}</span>}
                    {cert.id && <span> | ID: {cert.id}</span>}
                  </p>
                </div>
              )
            ))}
          </div>
        )}
        
        {generatedResume.awards.some(a => a.title) && (
          <div className="resume-section">
            <h2>Awards & Honors</h2>
            {generatedResume.awards.map((award, index) => (
              award.title && (
                <div key={index} className="award-item">
                  <h3>{award.title}</h3>
                  <p>
                    {award.issuer && <span>{award.issuer}</span>}
                    {award.date && <span> | {award.date}</span>}
                  </p>
                  {award.description && <p className="award-description">{award.description}</p>}
                </div>
              )
            ))}
          </div>
        )}
        
        {generatedResume.languages.some(l => l.name) && (
          <div className="resume-section">
            <h2>Languages</h2>
            <p>
              {generatedResume.languages.filter(l => l.name).map((lang, index, array) => (
                <span key={index}>
                  {lang.name}{lang.level && ` (${lang.level})`}
                  {index < array.length - 1 ? ' • ' : ''}
                </span>
              ))}
            </p>
          </div>
        )}
        
        {generatedResume.interests.filter(i => i).length > 0 && (
          <div className="resume-section">
            <h2>Interests</h2>
            <p>{generatedResume.interests.filter(i => i).join(' • ')}</p>
          </div>
        )}
      </div>
    );
  };

  const renderMinimalistTemplate = () => {
    if (!generatedResume) return null;
    
    return (
      <div className="resume-minimalist">
        <div className="resume-header-minimalist">
          <h1>{generatedResume.name}</h1>
          {generatedResume.headline && <p className="headline">{generatedResume.headline}</p>}
          <div className="contact-info">
            {generatedResume.location && <span>{generatedResume.location} | </span>}
            {generatedResume.phone && <span>{generatedResume.phone} | </span>}
            {generatedResume.email && <span>{generatedResume.email} | </span>}
            {generatedResume.linkedin && <span>{generatedResume.linkedin} | </span>}
            {generatedResume.github && <span>{generatedResume.github} | </span>}
            {generatedResume.portfolio && <span>{generatedResume.portfolio}</span>}
          </div>
        </div>
        
        <div className="resume-body-minimalist">
          <div className="resume-section-minimalist">
            <h2>Summary</h2>
            <p>{generatedResume.summary}</p>
          </div>
          
          <div className="resume-section-minimalist">
            <h2>Skills</h2>
            <div className="skills-content-minimalist">
              {generatedResume.skills.technical.filter(s => s).length > 0 && (
                <div className="skills-category-minimalist">
                  <span className="skills-label">Technical:</span>
                  <span>{generatedResume.skills.technical.filter(s => s).join(' • ')}</span>
                </div>
              )}
              
              {generatedResume.skills.soft.filter(s => s).length > 0 && (
                <div className="skills-category-minimalist">
                  <span className="skills-label">Soft:</span>
                  <span>{generatedResume.skills.soft.filter(s => s).join(' • ')}</span>
                </div>
              )}
              
              {generatedResume.skills.tools.filter(s => s).length > 0 && (
                <div className="skills-category-minimalist">
                  <span className="skills-label">Tools:</span>
                  <span>{generatedResume.skills.tools.filter(s => s).join(' • ')}</span>
                </div>
              )}
            </div>
          </div>
          
          <div className="resume-section-minimalist">
            <h2>Experience</h2>
            {generatedResume.experience.map((exp, index) => (
              exp.company && (
                <div key={index} className="experience-item-minimalist">
                  <div className="job-header-minimalist">
                    <div className="job-title-company">
                      <h3>{exp.title} | {exp.company}</h3>
                    </div>
                    <div className="job-date-location">
                      <span>{exp.location} | {exp.startDate} - {exp.endDate}</span>
                    </div>
                  </div>
                  <ul className="achievements-minimalist">
                    {exp.achievements.filter(a => a).map((achievement, i) => (
                      <li key={i}>{achievement}</li>
                    ))}
                  </ul>
                </div>
              )
            ))}
          </div>
          
          {generatedResume.projects.some(p => p.title) && (
            <div className="resume-section-minimalist">
              <h2>Projects</h2>
              {generatedResume.projects.map((project, index) => (
                project.title && (
                  <div key={index} className="project-item-minimalist">
                    <div className="project-header-minimalist">
                      <h3>{project.title}</h3>
                      <div className="project-details">
                        {project.technologies && <span>{project.technologies} | </span>}
                        {project.startDate && project.endDate && <span>{project.startDate} - {project.endDate}</span>}
                      </div>
                    </div>
                    {project.description && <p>{project.description}</p>}
                    {project.achievements.filter(a => a).length > 0 && (
                      <ul>
                        {project.achievements.filter(a => a).map((achievement, i) => (
                          <li key={i}>{achievement}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )
              ))}
            </div>
          )}
          
          <div className="resume-section-minimalist">
            <h2>Education</h2>
            {generatedResume.education.map((edu, index) => (
              edu.institution && (
                <div key={index} className="education-item-minimalist">
                  <div className="edu-header-minimalist">
                    <h3>{edu.degree}</h3>
                    <span>{edu.institution} | {edu.location} | {edu.startYear} - {edu.endYear}</span>
                  </div>
                  {edu.fieldOfStudy && <p>{edu.fieldOfStudy}</p>}
                  {edu.gpa && <p>GPA: {edu.gpa}</p>}
                </div>
              )
            ))}
          </div>
          
          {generatedResume.certifications.some(c => c.name) && (
            <div className="resume-section-minimalist">
              <h2>Certifications</h2>
              <div className="certifications-list">
                {generatedResume.certifications.filter(c => c.name).map((cert, index) => (
                  <span key={index}>{cert.name}{index < generatedResume.certifications.filter(c => c.name).length - 1 ? ' • ' : ''}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderTraditionalTemplate = () => {
    if (!generatedResume) return null;
    
    return (
      <div className="resume-traditional">
        <div className="resume-header-traditional">
          <h1 className="name">{generatedResume.name}</h1>
          <div className="contact-info-traditional">
            <p>
              {generatedResume.location && <span>{generatedResume.location} | </span>}
              {generatedResume.phone && <span>{generatedResume.phone} | </span>}
              {generatedResume.email && <span>{generatedResume.email}</span>}
            </p>
            <p>
              {generatedResume.linkedin && <span>{generatedResume.linkedin} | </span>}
              {generatedResume.github && <span>{generatedResume.github} | </span>}
              {generatedResume.portfolio && <span>{generatedResume.portfolio}</span>}
            </p>
          </div>
        </div>
        
        <div className="resume-section-traditional">
          <h2 className="section-heading">OBJECTIVE</h2>
          <div className="section-content">
            <p>{generatedResume.summary}</p>
          </div>
        </div>
        
        <div className="resume-section-traditional">
          <h2 className="section-heading">SKILLS</h2>
          <div className="section-content">
            {generatedResume.skills.technical.filter(s => s).length > 0 && (
              <p><strong>Technical Skills:</strong> {generatedResume.skills.technical.filter(s => s).join(', ')}</p>
            )}
            {generatedResume.skills.soft.filter(s => s).length > 0 && (
              <p><strong>Soft Skills:</strong> {generatedResume.skills.soft.filter(s => s).join(', ')}</p>
            )}
            {generatedResume.skills.tools.filter(s => s).length > 0 && (
              <p><strong>Tools & Technologies:</strong> {generatedResume.skills.tools.filter(s => s).join(', ')}</p>
            )}
          </div>
        </div>
        
        <div className="resume-section-traditional">
          <h2 className="section-heading">PROFESSIONAL EXPERIENCE</h2>
          <div className="section-content">
            {generatedResume.experience.map((exp, index) => (
              exp.company && (
                <div key={index} className="experience-item-traditional">
                  <div className="job-header-traditional">
                    <div className="job-title-company">
                      <p className="job-title"><strong>{exp.title}, {exp.company}</strong></p>
                      <p className="job-location">{exp.location}</p>
                    </div>
                    <p className="job-date">{exp.startDate} - {exp.endDate}</p>
                  </div>
                  <ul className="job-achievements">
                    {exp.achievements.filter(a => a).map((achievement, i) => (
                      <li key={i}>{achievement}</li>
                    ))}
                  </ul>
                </div>
              )
            ))}
          </div>
        </div>
        
        <div className="resume-section-traditional">
          <h2 className="section-heading">EDUCATION</h2>
          <div className="section-content">
            {generatedResume.education.map((edu, index) => (
              edu.institution && (
                <div key={index} className="education-item-traditional">
                  <div className="edu-header-traditional">
                    <div className="degree-institution">
                      <p className="degree"><strong>{edu.degree}, {edu.institution}</strong></p>
                      <p className="institution-location">{edu.location}</p>
                    </div>
                    <p className="edu-date">{edu.startYear} - {edu.endYear}</p>
                  </div>
                  {edu.fieldOfStudy && <p className="field-of-study">{edu.fieldOfStudy}</p>}
                  {edu.gpa && <p className="gpa">GPA: {edu.gpa}</p>}
                </div>
              )
            ))}
          </div>
        </div>
        
        {(generatedResume.projects.some(p => p.title) || generatedResume.academicProjects.some(p => p.title)) && (
          <div className="resume-section-traditional">
            <h2 className="section-heading">PROJECTS</h2>
            <div className="section-content">
              {generatedResume.projects.map((project, index) => (
                project.title && (
                  <div key={index} className="project-item-traditional">
                    <div className="project-header-traditional">
                      <p className="project-title"><strong>{project.title}</strong></p>
                      <p className="project-date">{project.startDate} - {project.endDate}</p>
                    </div>
                    {project.technologies && <p className="project-tech">{project.technologies}</p>}
                    {project.description && <p className="project-description">{project.description}</p>}
                    {project.achievements.filter(a => a).length > 0 && (
                      <ul className="project-achievements">
                        {project.achievements.filter(a => a).map((achievement, i) => (
                          <li key={i}>{achievement}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )
              ))}
              
              {generatedResume.academicProjects.map((project, index) => (
                project.title && (
                  <div key={index} className="project-item-traditional">
                    <div className="project-header-traditional">
                      <p className="project-title"><strong>{project.title}</strong> - {project.course}</p>
                      <p className="project-date">{project.year}</p>
                    </div>
                    {project.technologies && <p className="project-tech">{project.technologies}</p>}
                    {project.description && <p className="project-description">{project.description}</p>}
                  </div>
                )
              ))}
            </div>
          </div>
        )}
        
        {(generatedResume.certifications.some(c => c.name) || generatedResume.languages.some(l => l.name)) && (
          <div className="resume-section-traditional">
            <h2 className="section-heading">ADDITIONAL INFORMATION</h2>
            <div className="section-content">
              {generatedResume.certifications.some(c => c.name) && (
                <p>
                  <strong>Certifications:</strong> {generatedResume.certifications.filter(c => c.name).map(c => c.name).join(', ')}
                </p>
              )}
              
              {generatedResume.languages.some(l => l.name) && (
                <p>
                  <strong>Languages:</strong> {generatedResume.languages.filter(l => l.name).map(l => `${l.name}${l.level ? ` (${l.level})` : ''}`).join(', ')}
                </p>
              )}
              
              {generatedResume.interests.filter(i => i).length > 0 && (
                <p>
                  <strong>Interests:</strong> {generatedResume.interests.filter(i => i).join(', ')}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="resume-builder-container my-5 py-5">
      <div className="main-content">
        <div className="left-panel">
          <h2>Resume Builder</h2>
          <div className="form-section">
            <h3>Personal Information</h3>
            <div className="form-row">
              <div className="form-field">
                <label htmlFor="name">Name <span className="required">*</span></label>
                <input 
                  type="text" 
                  id="name" 
                  name="name"
                  value={formData.name} 
                  onChange={(e) => handleInputChange(e)} 
                  placeholder="John Doe" 
                  required 
                />
              </div>
              <div className="form-field">
                <label htmlFor="headline">Professional Headline</label>
                <input 
                  type="text" 
                  id="headline" 
                  name="headline"
                  value={formData.headline} 
                  onChange={(e) => handleInputChange(e)} 
                  placeholder="Senior Software Engineer" 
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-field">
                <label htmlFor="location">Location</label>
                <input 
                  type="text" 
                  id="location" 
                  name="location"
                  value={formData.location} 
                  onChange={(e) => handleInputChange(e)} 
                  placeholder="San Francisco, CA" 
                />
              </div>
              <div className="form-field">
                <label htmlFor="phone">Phone</label>
                <input 
                  type="tel" 
                  id="phone" 
                  name="phone"
                  value={formData.phone} 
                  onChange={(e) => handleInputChange(e)} 
                  placeholder="(123) 456-7890" 
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-field">
                <label htmlFor="email">Email</label>
                <input 
                  type="email" 
                  id="email" 
                  name="email"
                  value={formData.email} 
                  onChange={(e) => handleInputChange(e)} 
                  placeholder="john.doe@example.com" 
                />
              </div>
              <div className="form-field">
                <label htmlFor="portfolio">Portfolio/Website</label>
                <input 
                  type="url" 
                  id="portfolio" 
                  name="portfolio"
                  value={formData.portfolio} 
                  onChange={(e) => handleInputChange(e)} 
                  placeholder="www.johndoe.com" 
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-field">
                <label htmlFor="linkedin">LinkedIn</label>
                <input 
                  type="text" 
                  id="linkedin" 
                  name="linkedin"
                  value={formData.linkedin} 
                  onChange={(e) => handleInputChange(e)} 
                  placeholder="linkedin.com/in/johndoe" 
                />
              </div>
              <div className="form-field">
                <label htmlFor="github">GitHub</label>
                <input 
                  type="text" 
                  id="github" 
                  name="github"
                  value={formData.github} 
                  onChange={(e) => handleInputChange(e)} 
                  placeholder="github.com/johndoe" 
                />
              </div>
            </div>
          </div>
          
          <div className="form-section">
            <h3>Target Job</h3>
            <div className="form-field">
              <label htmlFor="jobTitle">Target Job Title <span className="required">*</span></label>
              <input 
                type="text" 
                id="jobTitle" 
                name="jobTitle"
                value={formData.jobTitle} 
                onChange={(e) => handleInputChange(e)} 
                placeholder="Software Engineer" 
                required 
              />
            </div>
            <div className="form-field">
              <label htmlFor="jobDescription">Job Description (for ATS optimization)</label>
              <textarea 
                id="jobDescription" 
                name="jobDescription"
                value={formData.jobDescription} 
                onChange={(e) => handleInputChange(e)} 
                placeholder="Paste the job description here to optimize your resume for ATS" 
                rows="5"
              ></textarea>
            </div>
            <div className="form-buttons">
              <button 
                type="button" 
                className="button secondary" 
                onClick={generateProfessionalSummary}
              >
                Generate Professional Summary
              </button>
            </div>
            {summary && (
              <div className="generated-summary">
                <h4>Generated Summary</h4>
                <p>{summary}</p>
              </div>
            )}
          </div>
          
          {/* Additional form sections for Experience, Education, Skills, etc. would be implemented here */}
          
          <div className="form-buttons">
            <button 
              type="button" 
              className="button primary" 
              onClick={generateResume}
            >
              Generate Resume
            </button>
          </div>
        </div>
        
        <div className="right-panel">
          <div className="resume-controls">
            <div className="template-selector">
              <label htmlFor="template">Template:</label>
              <select 
                id="template" 
                value={selectedTemplate} 
                onChange={(e) => setSelectedTemplate(e.target.value)}
              >
                <option value="modern">Modern</option>
                <option value="minimalist">Minimalist</option>
                <option value="traditional">Traditional</option>
              </select>
            </div>
            <div className="download-options">
              <button 
                type="button" 
                className="button" 
                onClick={downloadPDF}
                disabled={!generatedResume}
              >
                Download PDF
              </button>
              <button 
                type="button" 
                className="button" 
                onClick={downloadDOCX}
                disabled={!generatedResume}
              >
                Download DOCX
              </button>
            </div>
          </div>
          
          <div className="resume-output">
            <div ref={resumeRef} className="resume-preview">
              {selectedTemplate === 'modern' && renderModernTemplate()}
              {selectedTemplate === 'minimalist' && renderMinimalistTemplate()}
              {selectedTemplate === 'traditional' && renderTraditionalTemplate()}
            </div>
          </div>
          
          <div className="ats-analyzer">
            <h3>ATS Analysis</h3>
            <div className="ats-controls">
              <textarea 
                placeholder="Copy and paste your resume text here for ATS analysis" 
                value={resumeText}
                onChange={(e) => setResumeText(e.target.value)}
                rows="5"
              ></textarea>
              <div className="ats-buttons">
                <button 
                  type="button" 
                  className="button secondary" 
                  onClick={calculateATSScore}
                  disabled={!resumeText || !formData.jobDescription}
                >
                  Analyze ATS Score
                </button>
                <button 
                  type="button" 
                  className="button secondary" 
                  onClick={optimizeResume}
                  disabled={!resumeText || !formData.jobDescription || isOptimizing}
                >
                  {isOptimizing ? 'Optimizing...' : 'Optimize Resume'}
                </button>
              </div>
            </div>
            
            {atsScore !== null && (
              <div className="ats-results">
                <div className="ats-score">
                  <h4>ATS Score: {atsScore}%</h4>
                  <div className="progress-bar">
                    <div 
                      className="progress" 
                     style={{ width: `${atsScore}%`, backgroundColor: atsScore > 75 ? '#4CAF50' : atsScore > 50 ? '#FFC107' : '#F44336' }}
                    ></div>
                  </div>
                </div>
                
                <div className="keyword-analysis">
                  <div className="matched-keywords">
                    <h4>Matched Keywords ({matchedKeywords.length})</h4>
                    <div className="keyword-list">
                      {matchedKeywords.map((keyword, index) => (
                        <span key={index} className="keyword matched">{keyword}</span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="missed-keywords">
                    <h4>Missing Keywords ({missedKeywords.length})</h4>
                    <div className="keyword-list">
                      {missedKeywords.map((keyword, index) => (
                        <span key={index} className="keyword missed">{keyword}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {optimizationSuggestions && (
              <div className="optimization-suggestions">
                <h4>Optimization Suggestions</h4>
                
                {optimizationSuggestions.addKeywords.length > 0 && (
                  <div className="suggestion-section">
                    <h5>Add These Keywords</h5>
                    <div className="suggestion-keywords">
                      {optimizationSuggestions.addKeywords.slice(0, 10).map((keyword, index) => (
                        <span key={index} className="suggestion-keyword">{keyword}</span>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="suggestion-section">
                  <h5>Improve Your Summary</h5>
                  <p>{optimizationSuggestions.improveSummary}</p>
                </div>
                
                {optimizationSuggestions.sectionImprovements.length > 0 && (
                  <div className="suggestion-section">
                    <h5>Section Improvements</h5>
                    <ul>
                      {optimizationSuggestions.sectionImprovements.map((suggestion, index) => (
                        <li key={index}>{suggestion}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                <div className="suggestion-section">
                  <h5>Format Improvements</h5>
                  <ul>
                    {optimizationSuggestions.formatImprovements.map((suggestion, index) => (
                      <li key={index}>{suggestion}</li>
                    ))}
                  </ul>
                </div>
                </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResumeBuilder2;

              